import { useMemo } from "react";
import type React from "react";

/**
 * Langer Druck auf eine Mailzeile (Handy) — startet den Auswahlmodus wie in
 * Outlook iOS.
 *
 * Warum nicht `contextmenu`: iOS Safari feuert bei langem Druck KEIN
 * `contextmenu`, nur Android Chrome. Deshalb ein eigener Zeitgeber über
 * Pointer-Events, nur für Finger und Stift (`pointerType !== "mouse"`).
 *
 * Wer den Finger dabei bewegt, scrollt oder wischt (Wisch-Löschen) — dann
 * bricht der Zeitgeber ab. Hat der lange Druck ausgelöst, schluckt die Zeile
 * den anschließenden `click`, sonst öffnete sich die Mail gleich mit.
 */
export const LANGER_DRUCK_MS = 480;
export const LANGER_DRUCK_TOLERANZ_PX = 10;

export interface LangerDruckUhr {
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (id: unknown) => void;
}

export interface LangerDruck {
    start: (x: number, y: number, ausloesen: () => void) => void;
    bewegt: (x: number, y: number) => void;
    ende: () => void;
    /** Einmalig `true` nach einem ausgelösten langen Druck (dann den Klick verwerfen). */
    klickSchlucken: () => boolean;
}

export function erzeugeLangerDruck(
    uhr: LangerDruckUhr = {
        setTimeout: (fn, ms) => window.setTimeout(fn, ms),
        clearTimeout: (id) => window.clearTimeout(id as number),
    },
    dauerMs = LANGER_DRUCK_MS,
    toleranzPx = LANGER_DRUCK_TOLERANZ_PX,
): LangerDruck {
    let timer: unknown = null;
    let start: { x: number; y: number } | null = null;
    let ausgeloest = false;
    const stopp = () => {
        if (timer !== null) uhr.clearTimeout(timer);
        timer = null;
        start = null;
    };
    return {
        start(x, y, ausloesen) {
            stopp();
            ausgeloest = false;
            start = { x, y };
            timer = uhr.setTimeout(() => {
                timer = null;
                start = null;
                ausgeloest = true;
                ausloesen();
            }, dauerMs);
        },
        bewegt(x, y) {
            if (!start) return;
            if (Math.hypot(x - start.x, y - start.y) > toleranzPx) stopp();
        },
        ende: stopp,
        klickSchlucken() {
            const v = ausgeloest;
            ausgeloest = false;
            return v;
        },
    };
}

export type LangerDruckProps = Pick<
    React.HTMLAttributes<HTMLElement>,
    "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel" | "onPointerLeave"
>;

/**
 * EIN Zeitgeber für die ganze Liste (es drückt immer nur ein Finger auf eine
 * Zeile). `fuer(ausloesen)` liefert die Pointer-Handler einer Zeile.
 */
export function useLangerDruck(): { fuer: (ausloesen: () => void) => LangerDruckProps; klickSchlucken: () => boolean } {
    return useMemo(() => {
        const ld = erzeugeLangerDruck();
        return {
            fuer: (ausloesen: () => void): LangerDruckProps => ({
                onPointerDown: (e) => {
                    if (e.pointerType === "mouse") return;
                    ld.start(e.clientX, e.clientY, () => {
                        // Kurzes Rückmelden, wo das Gerät es kann (Android).
                        try {
                            navigator.vibrate?.(12);
                        } catch {
                            /* egal */
                        }
                        ausloesen();
                    });
                },
                onPointerMove: (e) => ld.bewegt(e.clientX, e.clientY),
                onPointerUp: () => ld.ende(),
                onPointerCancel: () => ld.ende(),
                onPointerLeave: () => ld.ende(),
            }),
            klickSchlucken: ld.klickSchlucken,
        };
    }, []);
}
